/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from "../config/envConfig";
import '../styles/CountCard.css';
import { useQuery } from '@tanstack/react-query';

// Initialize API helper instance with configuration
const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * CountCard component - displays a statistic count with label and icon.
 * Fetches its own data based on the stat_type props.
 * Displays loading state by hiding self until data/error received.
 * Props:
 * - icon (string): Bootstrap icon class name to display
 * - label (string): Text label for the stat
 * - stat_type (string): API query type, e.g., "applications"
 * - color (string): Optional color hex code for styling
 */
function CountCard({ icon, label, stat_type, color }) {
  const { theme } = useTheme();

  const [count, setCount] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const queryKey = ['dashboardStat', stat_type];

  const {
    data: statData,
    error: statError,
    isLoading: statLoading,
    isFetching: statFetching,
  } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      if (!stat_type) return null;
      const resp = await apiHelper.apiCall(`/arcee/admin/dashboard/stats/?q=${stat_type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
      });
      if (!resp.ok) {
        let body = null;
        try { body = await resp.json(); } catch (e) { /* ignore */ }
        const msg = (body && body.error) ? body.error : `Failed to fetch ${stat_type} (${resp.status})`;
        throw new Error(msg);
      }
      return resp.json();
    },
    enabled: !!stat_type,
    staleTime: 30_000,
    retry: 1,
  });

  useEffect(() => {
    if (statLoading) {
      setLoading(true);
      setError(null);
      return;
    }
    if (statError) {
      setLoading(false);
      setCount(null);
      setError(statError.message || 'Error, please refresh');
      return;
    }
    if (statData) {
      setLoading(false);
      setError(null);
      setCount(typeof statData.count !== 'undefined' ? statData.count : null);
    }
  }, [statLoading, statData, statError]);

  if (loading) return null; // Hide card until data arrives or error happens

  return (
    <div
      className="count-card d-flex align-items-center p-3 shadow-sm"
      style={{
        minWidth: 220,
        minHeight: 120,
        background: '#fff',
        borderLeft: `5px solid ${color || theme.primary}`,
        borderRadius: theme.cardRadius,
      }}
    >
      <div
        className="icon-wrapper d-flex align-items-center justify-content-center me-3"
        style={{
          background: `${color || theme.primary}15`,
          borderRadius: '50%',
          width: 48,
          height: 48,
        }}
      >
        <i className={`bi ${icon} fs-2`} style={{ color: color || theme.primary }} />
      </div>

      <div className="d-flex flex-column flex-grow-1 text-start">
        {!error && (
          <span style={{ fontWeight: 700, fontSize: 24, color: theme.primary }}>
            {count}
          </span>
        )}

        <span style={{ color: '#444', fontSize: 15, marginTop: error ? 0 : '4px' }}>
          {label}
        </span>

        {error && (
          <span
            style={{
              color: 'red',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              marginTop: 4,
            }}
          >
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

export default CountCard;
